// Rich Assistant output (NEXUS-763): split a reply into markdown and rich blocks (```mermaid, ```svg / inline <svg>,
// ```html), and sanitize SVG. Everything here is pure string work (no DOM) so it is unit-testable and cannot be
// bypassed by a parser quirk: SVG is rebuilt from an allow-list of elements and attributes, never filtered by regex
// and passed through.

export type RichKind = 'mermaid' | 'svg' | 'html';
export type Segment = { type: 'md'; text: string } | { type: 'rich'; kind: RichKind; code: string };

const RICH_LANGS: Record<string, RichKind> = { mermaid: 'mermaid', svg: 'svg', html: 'html' };

/** Split reply text into markdown segments and rich blocks. Fences other than the three rich ones stay in markdown. */
export function splitRich(src: string): Segment[] {
  const lines = String(src ?? '').split('\n');
  const out: Segment[] = [];
  let md: string[] = [];
  const flush = () => { if (md.length) { pushMarkdown(out, md.join('\n')); md = []; } };
  let i = 0;
  while (i < lines.length) {
    const open = /^( {0,3})(`{3,}|~{3,})\s*([A-Za-z0-9_-]*)[^\n`]*$/.exec(lines[i]);
    if (!open) { md.push(lines[i]); i++; continue; }
    const fence = open[2];
    let j = i + 1;
    const body: string[] = [];
    const closer = new RegExp(`^ {0,3}\\${fence[0]}{${fence.length},}\\s*$`);
    while (j < lines.length && !closer.test(lines[j])) { body.push(lines[j]); j++; }
    const kind = RICH_LANGS[open[3].toLowerCase()];
    if (kind) { flush(); out.push({ type: 'rich', kind, code: body.join('\n') }); }
    else { flush(); out.push({ type: 'md', text: lines.slice(i, Math.min(j + 1, lines.length)).join('\n') }); }   // inline <svg> is not looked for inside code
    i = j + 1;
  }
  flush();
  return out;
}

/** Inline <svg>…</svg> in prose becomes an svg block (nested <svg> handled by depth counting). */
function pushMarkdown(out: Segment[], text: string): void {
  let rest = text;
  for (;;) {
    const start = rest.search(/<svg[\s>/]/i);
    if (start < 0) break;
    const tag = /<(\/?)svg[\s>/]/gi;
    tag.lastIndex = start;
    let depth = 0, end = -1, m: RegExpExecArray | null;
    while ((m = tag.exec(rest))) {
      depth += m[1] ? -1 : 1;
      if (depth === 0) { end = rest.indexOf('>', m.index) + 1; break; }
    }
    if (end <= 0) break;
    if (start > 0) out.push({ type: 'md', text: rest.slice(0, start) });
    out.push({ type: 'rich', kind: 'svg', code: rest.slice(start, end) });
    rest = rest.slice(end);
  }
  if (rest.trim()) out.push({ type: 'md', text: rest });
}

// ---- SVG sanitizer ----------------------------------------------------------------------------------------------

const ELEMENTS = [
  'svg', 'g', 'defs', 'symbol', 'use', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'text', 'tspan',
  'textPath', 'title', 'desc', 'linearGradient', 'radialGradient', 'stop', 'pattern', 'clipPath', 'mask', 'marker', 'filter',
  'feGaussianBlur', 'feOffset', 'feColorMatrix', 'feMerge', 'feMergeNode', 'feBlend', 'feFlood', 'feComposite', 'feDropShadow', 'style',
];
const ELEMENT_BY_LOWER = new Map(ELEMENTS.map((e) => [e.toLowerCase(), e]));
/** Dropped together with everything inside them. */
const DROP_WITH_CONTENT = new Set(['script', 'foreignobject', 'iframe', 'object', 'embed', 'animate', 'animatetransform', 'animatemotion', 'set',
  'handler', 'listener', 'image', 'audio', 'video', 'canvas', 'link', 'meta', 'base', 'template', 'noscript']);

const ATTRIBUTES = new Set([
  'id', 'class', 'style', 'viewbox', 'xmlns', 'xmlns:xlink', 'width', 'height', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'd', 'points', 'transform', 'transform-origin', 'fill', 'fill-opacity', 'fill-rule', 'stroke', 'stroke-width', 'stroke-opacity',
  'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-miterlimit', 'opacity', 'color',
  'font-family', 'font-size', 'font-weight', 'font-style', 'text-anchor', 'dominant-baseline', 'alignment-baseline', 'letter-spacing',
  'text-decoration', 'dx', 'dy', 'rotate', 'textlength', 'lengthadjust', 'offset', 'stop-color', 'stop-opacity', 'gradientunits',
  'gradienttransform', 'spreadmethod', 'fx', 'fy', 'patternunits', 'patterntransform', 'patterncontentunits', 'clip-path', 'clip-rule',
  'clippathunits', 'mask', 'maskunits', 'maskcontentunits', 'marker-start', 'marker-mid', 'marker-end', 'markerwidth', 'markerheight',
  'markerunits', 'refx', 'refy', 'orient', 'preserveaspectratio', 'filter', 'filterunits', 'primitiveunits', 'stddeviation', 'in', 'in2',
  'result', 'mode', 'values', 'type', 'flood-color', 'flood-opacity', 'operator', 'k1', 'k2', 'k3', 'k4', 'color-interpolation-filters',
  'visibility', 'display', 'vector-effect', 'role', 'aria-label', 'aria-hidden', 'aria-labelledby', 'focusable', 'version', 'startoffset',
  'href', 'xlink:href',
]);

const NAMED: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
function decodeEntities(v: string): string {
  return v.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const n = e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
    }
    return NAMED[e.toLowerCase()] ?? m;
  });
}
const CAMEL = new Map(['viewBox', 'textLength', 'lengthAdjust', 'gradientUnits', 'gradientTransform', 'spreadMethod', 'patternUnits',
  'patternTransform', 'patternContentUnits', 'clipPathUnits', 'maskUnits', 'maskContentUnits', 'markerWidth', 'markerHeight', 'markerUnits',
  'refX', 'refY', 'preserveAspectRatio', 'filterUnits', 'primitiveUnits', 'stdDeviation', 'startOffset'].map((a) => [a.toLowerCase(), a]));
const escAttr = (s: string): string => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** True when a value (decoded) carries a scripting or external-load vector. Only url(#fragment) references pass. */
function dangerous(v: string, css = false): boolean {
  const flat = v.replace(/[\u0000- \u007f-\u009f]+/g, '').toLowerCase();
  if (/javascript:|vbscript:|data:|expression\(|@import|behavior:|-moz-binding|</.test(flat) || (!css && flat.includes('>'))) return true;
  for (const m of v.matchAll(/url\(([^)]*)\)?/gi)) if (!/^\s*['"]?\s*#/.test(m[1])) return true;
  return false;
}
/** CSS for <style> / style="": same test; additionally no external urls at all. */
function safeCss(css: string): string | null {
  const d = decodeEntities(css);
  return dangerous(d, true) || /\\/.test(d) ? null : d;
}

const TAG = /<(\/?)([A-Za-z][A-Za-z0-9:._-]*)((?:\s+[^\s"'<>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'<>=`]+))?)*)\s*(\/?)>/y;
const ATTR = /([^\s"'<>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'<>=`]+)))?/g;

function cleanAttrs(tag: string, raw: string): string {
  let out = '';
  const seen = new Set<string>();
  for (const m of raw.matchAll(ATTR)) {
    const name = m[1].toLowerCase();
    if (name.startsWith('on') || !ATTRIBUTES.has(name) || seen.has(name)) continue;
    const value = decodeEntities(m[2] ?? m[3] ?? m[4] ?? '');
    if (name === 'href' || name === 'xlink:href') {
      if (!/^#[\w.:-]+$/.test(value.trim()) || !['use', 'lineargradient', 'radialgradient', 'pattern', 'textpath'].includes(tag.toLowerCase())) continue;
    } else if (name === 'style') {
      const css = safeCss(value);
      if (css === null) continue;
      seen.add(name); out += ` style="${escAttr(css)}"`; continue;
    } else if (dangerous(value)) continue;
    seen.add(name);
    out += ` ${CAMEL.get(name) ?? name}="${escAttr(value)}"`;
  }
  return out;
}

/** Rebuild the first <svg> in `src` from allow-listed elements/attributes. Returns '' when nothing usable remains. */
export function sanitizeSvg(src: string): string {
  const input = String(src ?? '');
  const start = input.search(/<svg[\s>/]/i);
  if (start < 0) return '';
  const s = input.slice(start).replace(/<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\?[\s\S]*?\?>|<!DOCTYPE[^>]*>/gi, '');
  let out = '';
  let i = 0, dropDepth = 0, dropName = '', inStyle = false, rootOpen = false, rootDone = false;
  const stack: string[] = [];
  while (i < s.length && !rootDone) {
    if (s[i] !== '<') {
      const next = s.indexOf('<', i);
      const text = s.slice(i, next < 0 ? s.length : next);
      i = next < 0 ? s.length : next;
      if (dropDepth) continue;
      if (inStyle) { const css = safeCss(text); if (css !== null) out += css.replace(/&/g, '&amp;'); }
      else out += text.replace(/>/g, '&gt;');
      continue;
    }
    TAG.lastIndex = i;
    const m = TAG.exec(s);
    if (!m) { if (!dropDepth) out += '&lt;'; i++; continue; }
    i = TAG.lastIndex;
    const closing = m[1] === '/', lname = m[2].toLowerCase(), selfClose = m[4] === '/';
    if (dropDepth) {
      if (lname === dropName) dropDepth += closing ? -1 : selfClose ? 0 : 1;
      continue;
    }
    if (DROP_WITH_CONTENT.has(lname)) {
      if (!closing && !selfClose) { dropDepth = 1; dropName = lname; }
      continue;
    }
    const canon = ELEMENT_BY_LOWER.get(lname);
    if (!canon) continue;   // unknown element: unwrapped, its text and known children are kept
    if (closing) {
      const at = stack.lastIndexOf(canon);
      if (at < 0) continue;
      while (stack.length > at) { const t = stack.pop() as string; out += `</${t}>`; if (t === 'style') inStyle = false; }
      if (!stack.length) rootDone = true;
      continue;
    }
    if (!rootOpen && canon !== 'svg') continue;
    let attrs = cleanAttrs(canon, m[3] ?? '');
    if (!rootOpen) {
      rootOpen = true;
      if (!/\sxmlns="/.test(attrs)) attrs = ` xmlns="http://www.w3.org/2000/svg"${attrs}`;
    }
    if (selfClose) { out += `<${canon}${attrs}/>`; if (!stack.length) rootDone = true; continue; }
    out += `<${canon}${attrs}>`;
    stack.push(canon);
    if (canon === 'style') inStyle = true;
  }
  while (stack.length) { const t = stack.pop() as string; out += `</${t}>`; }
  return out.startsWith('<svg') ? out : '';
}

/** Standalone, script-free document for an ```html block (shown in `<iframe sandbox="" srcdoc>`; the CSP is a second lock). */
export function htmlDocument(code: string): string {
  const csp = "default-src 'none'; img-src data:; style-src 'unsafe-inline'";
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}">` +
    `<base target="_blank"><style>body{font:14px/1.45 -apple-system,system-ui,sans-serif;margin:12px;color:#111;background:#fff}</style></head><body>${code}</body></html>`;
}

/** Safe download name for a saved block. */
export function blockFileName(kind: RichKind, n = 1): string {
  return `${kind === 'mermaid' ? 'diagram' : kind}-${n}.${kind === 'html' ? 'html' : 'svg'}`;
}
